import {
  type ChangeEvent,
  type ComponentPropsWithRef,
  useId,
  useState,
} from 'react';

import { useAutocompleteSearch } from './AutocompleteSearch.context';
import styles from './Search.module.css';
import { type SearchButtonProps, type SearchSize } from './Search.types';
import { SearchSuggestions } from './SearchSuggestions';
import { useCombinedRef } from '../../hooks';
import { createTexts, useTranslation } from '../../i18n';
import {
  cn,
  derivativeIdGenerator,
  spaceSeparatedIdListGenerator,
} from '../../utils';
import { createClearChangeEvent } from '../../utils/createClearChangeEvent';
import { Button } from '../Button';
import { ClearButton } from '../helpers/ClearButton';
import { Input, type InputProps } from '../helpers/Input';
import inputStyles from '../helpers/Input/Input.module.css';
import { Icon } from '../Icon';
import { SearchIcon } from '../Icon/icons';
import { renderInputMessage } from '../InputMessage';
import { Box, Grid, HStack } from '../layout';
import { renderLabel } from '../Typography/Label/Label.utils';
import typographyStyles from '../Typography/typographyStyles.module.css';
import { VisuallyHidden } from '../VisuallyHidden';

export type SearchProps = Pick<InputProps, 'tip' | 'label'> & {
  /**Størrelsen på komponenten. */
  componentSize?: SearchSize;
  /**Props for søkeknappen. */
  buttonProps?: SearchButtonProps;
  /**Om søkeikonet skal vises. */
  showIcon?: boolean;
} & Pick<InputProps, 'width'> &
  Omit<ComponentPropsWithRef<'input'>, 'width' | 'height'>;

export const Search = ({
  componentSize = 'medium',
  buttonProps,
  showIcon = true,
  name,
  label,
  tip,
  id,
  value,
  width,
  onChange,
  className,
  style,
  'aria-describedby': ariaDescribedby,
  ref,
  ...rest
}: SearchProps) => {
  const generatedId = useId();
  const uniqueId = id ?? `${generatedId}-searchInput`;
  const tipId = derivativeIdGenerator(uniqueId, 'tip');
  const suggestionsId = derivativeIdGenerator(uniqueId, 'suggestions');
  const suggestionsDescriptionId = derivativeIdGenerator(
    uniqueId,
    'suggestions-description',
  );

  const { t } = useTranslation();

  const [hasValue, setHasValue] = useState(!!value);

  const context = useAutocompleteSearch();

  const combinedRef = context.inputRef
    ? useCombinedRef(context.inputRef, ref)
    : ref;

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setHasValue(e.target.value !== '');

    context?.onValueChange?.(e);
    onChange?.(e);
  };

  const clearInput = () => {
    const clearChangeEvent = createClearChangeEvent<HTMLInputElement>(uniqueId);
    handleChange(clearChangeEvent);
  };

  const {
    label: buttonLabel,
    onClick,
    ...otherButtonProps
  } = buttonProps ?? {};

  const hasSuggestions = !!context.suggestions;
  const showSearchButton = !!buttonProps && !!onClick;
  const inputGroup = (
    <HStack
      position="relative"
      width={!showSearchButton ? width : undefined}
      className={cn(
        !showSearchButton && className,
        typographyStyles[`body-short-${componentSize}`],
        inputStyles['input-group'],
      )}
    >
      {showIcon && (
        <Icon
          icon={SearchIcon}
          iconSize="component"
          className={cn(
            inputStyles['input-group__absolute-el'],
            inputStyles[`input-group__absolute-el--${componentSize}`],
          )}
        />
      )}
      <Box
        as={Input}
        {...rest}
        ref={combinedRef}
        name={name}
        type="search"
        id={uniqueId}
        aria-describedby={spaceSeparatedIdListGenerator([
          tip ? tipId : undefined,
          context.suggestions ? suggestionsDescriptionId : undefined,
          ariaDescribedby,
        ])}
        value={context.inputValue ?? value}
        onChange={handleChange}
        autoComplete="off"
        aria-autocomplete={hasSuggestions ? 'list' : undefined}
        aria-controls={hasSuggestions ? suggestionsId : undefined}
        aria-expanded={context.showSuggestions}
        role={hasSuggestions ? 'combobox' : undefined}
        width="100%"
        className={cn(
          styles.input,
          inputStyles[`input--${componentSize}`],
          showIcon && inputStyles[`input-with-icon--${componentSize}`],
          inputStyles[`input-with-btn-right--${componentSize}`],
        )}
      />
      {hasSuggestions && (
        <>
          <SearchSuggestions
            id={suggestionsId}
            ref={context.suggestionsRef}
            searchId={uniqueId}
            onSuggestionClick={context.onSugggestionClick}
            suggestions={context.suggestions}
            showSuggestions={context.showSuggestions}
          />
          <VisuallyHidden id={suggestionsDescriptionId}>
            {t(texts.useArrowKeys)}
          </VisuallyHidden>
        </>
      )}
      {hasValue && (
        <ClearButton
          size={componentSize}
          aria-label={t(texts.clearSearch)}
          onClick={clearInput}
        />
      )}
    </HStack>
  );
  return (
    <div>
      {renderLabel({ htmlFor: uniqueId, label })}
      <div>
        {showSearchButton ? (
          <Grid
            defaultPageLayout={false}
            className={className}
            width={width}
            columnGap="x0.5"
            gridTemplateColumns="1fr auto"
            style={style}
          >
            {inputGroup}
            <Button
              size={componentSize}
              onClick={onClick}
              {...otherButtonProps}
            >
              {buttonLabel ?? t(texts.search)}
            </Button>
          </Grid>
        ) : (
          inputGroup
        )}
        {renderInputMessage({ tip, tipId })}
      </div>
    </div>
  );
};

Search.displayName = 'Search';

const texts = createTexts({
  clearSearch: {
    nb: 'Tøm søk',
    no: 'Tøm søk',
    nn: 'Tøm søk',
    en: 'Clear search',
    se: 'Gurre ohcama',
  },
  search: {
    nb: 'Søk',
    no: 'Søk',
    nn: 'Søk',
    en: 'Search',
    se: 'Ohcan',
  },
  useArrowKeys: {
    nb: 'Bruk piltastene for å navigere i forslagene når listen er utvidet',
    no: 'Bruk piltastene for å navigere i forslagene når listen er utvidet',
    nn: 'Bruk piltastane for å navigere i forslaga når lista er utvida',
    en: 'Use the arrow keys to navigate suggestions when the list is expanded',
    se: 'Deatte njuollaboalu ohccat árvalusaid listtus mii lea viiddiduvvon',
  },
});
